
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileNavigation } from '@/components/mobile/MobileNavigation';
import { OfflineIndicator } from '@/components/mobile/OfflineIndicator';
import { PullToRefresh } from '@/components/mobile/PullToRefresh';
import LimitedOfferBanner from '@/components/conversion/LimitedOfferBanner';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import SkipLinks from "@/components/common/SkipLinks";
import Navbar from "@/components/Navbar";
import Chatbot from "./components/Chatbot";
import { CartProvider } from "./contexts/CartContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ABTestProvider } from "./contexts/ABTestContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import CosmicLayout from "@/components/layouts/CosmicLayout";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { referralTracking } from "@/utils/referralTracking";
import { useUserActivity } from "@/hooks/useUserActivity";
import { usePageTracking } from "@/hooks/usePageTracking";
import { initializeProduction } from "@/utils/deploymentConfig";
import { initPerformanceOptimizations } from "@/utils/performanceOptimization";
import { usePresenceTracking } from "@/hooks/usePresenceTracking";

// Critical pages - load immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AuthConfirmed from "./pages/AuthConfirmed";
import Unsubscribe from "./pages/Unsubscribe";
import Cart from "./pages/Cart";
import NotFound from "./pages/NotFound";

// Heavy pages - lazy load for performance
const Admin = lazy(() => import("./pages/Admin"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));

// Light pages - lazy load
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Shipping = lazy(() => import("./pages/Shipping"));
const Ingredients = lazy(() => import("./pages/Ingredients"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Referral = lazy(() => import("./pages/Referral"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
      onError: (error: any) => {
        console.error('Mutation error:', error);
        if (typeof window !== 'undefined') {
          const errorEvent = new CustomEvent('mutation-error', {
            detail: { error, timestamp: Date.now() }
          });
          window.dispatchEvent(errorEvent);
        }
      },
    },
  },
});

const AppContent: React.FC = () => {
  const isMobile = useIsMobile();
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  
  // Track user activity
  useUserActivity();
  
  // Page analytics tracking
  usePageTracking();
  
  // Real-time presence tracking for active users
  usePresenceTracking();

  useEffect(() => {
    // Initialize referral tracking on app mount
    referralTracking.initialize();
    
    // Initialize production monitoring
    initializeProduction();
    
    // Initialize performance optimizations (preconnects, etc.)
    initPerformanceOptimizations();
  }, []);

  const mainMarginTop = isBannerVisible ? 'mt-[140px]' : 'mt-[80px]';

  return (
    <CosmicLayout>
      <SkipLinks />
      <LimitedOfferBanner onVisibilityChange={setIsBannerVisible} />
      <Navbar isBannerVisible={isBannerVisible} />
      <OfflineIndicator />
      <main 
        id="main-content" 
        role="main"
        className={`${isMobile ? 'pb-20' : ''} ${mainMarginTop} transition-all duration-300`}
      >
        <PullToRefresh onRefresh={async () => {
          try {
            await queryClient.invalidateQueries();
          } catch (error) {
            console.log('Refresh failed:', error);
          }
        }}>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:productId" element={<ProductDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/ingredients" element={<Ingredients />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/referral" element={<Referral />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/confirmed" element={<AuthConfirmed />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/:orderId" element={<OrderDetail />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/shipping" element={<Shipping />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </PullToRefresh>
      </main>
      {isMobile && <MobileNavigation />}
      <Chatbot />
      <Toaster />
      <Sonner />
    </CosmicLayout>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CartProvider>
            <ABTestProvider>
              <AppContent />
            </ABTestProvider>
          </CartProvider>
        </AuthProvider>
      </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
